/**
 * Tier Configuration Constants for Olvidadme
 *
 * Sprint 03: Service-Based Tier Refactoring
 * Implementing Tier-Based Feature Access Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Centralized configuration for service tiers and their features
 *
 * Precios centralizados en: src/utils/constants/pricing.js
 *
 * Key Changes:
 * - 1 case per user restriction (casesPerUser: 1)
 * - URL-based limits instead of platforms
 * - Service-specific features and capabilities
 *
 * Connected with:
 * - User.serviceTier model field (backend)
 * - Case.serviceTier model field (backend)
 * - Auth store user.serviceTier (frontend)
 * - Payment amounts in Case model
 */

import { PRICING } from './pricing.js'

// Tier names enum for consistency
export const TIER_NAMES = {
  OLVIDO: 'olvido',
  OLVIDO_POSTMORTEM: 'olvido_postmortem',
  TESTAMENTO: 'testamento'
}

// Tier display configuration - Service-based naming
export const TIER_DISPLAY = {
  olvido: {
    label: 'Derecho al Olvido',
    badgeIcon: 'ShieldExclamationIcon',
    color: 'teal',
    gradient: 'from-teal-600 to-teal-500',
    bgClass: 'bg-teal-50',
    borderClass: 'border-teal-200',
    textClass: 'text-teal-700'
  },
  olvido_postmortem: {
    label: 'Derecho al Olvido Post-Mortem',
    badgeIcon: 'HeartIcon',
    color: 'purple',
    gradient: 'from-purple-600 to-purple-500',
    bgClass: 'bg-purple-50',
    borderClass: 'border-purple-200',
    textClass: 'text-purple-700'
  },
  testamento: {
    label: 'Testamento Digital',
    badgeIcon: 'DocumentTextIcon',
    color: 'amber',
    gradient: 'from-amber-500 to-amber-400',
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-200',
    textClass: 'text-amber-700'
  }
}

// Main tier configuration with limits and features
export const TIER_LIMITS = {
  olvido: {
    // Pricing (from centralized pricing.js)
    price: PRICING.OLVIDO.price,
    priceLabel: PRICING.OLVIDO.priceLabel,

    // URL limits (replaces platform limits)
    urls: PRICING.OLVIDO.urlLimit,
    urlsLabel: 'Hasta 5 URLs',

    // Case limits - 1 case per user
    casesPerUser: 1,
    casesPerUserLabel: '1 caso único',

    // Case management capabilities
    canCreateCases: true,
    canManageCases: true,

    // Support
    supportResponseTime: 'Lo antes posible',
    supportType: 'Email estándar',

    // Features available
    features: [
      'url_management',
      'case_creation',
      'document_generation',
      'timeline_tracking',
      'email_support'
    ],

    // Features labels for UI
    featuresLabels: [
      'Gestión de hasta 5 URLs',
      'Creación de 1 caso',
      'Generación de documentos legales',
      'Seguimiento de timeline',
      'Soporte por email',
      'Solicitudes GDPR automatizadas'
    ],

    // Restrictions
    restrictions: [
      'Solo 1 caso por usuario',
      'Máximo 5 URLs',
      'Sin soporte prioritario',
      'Sin información de fallecido'
    ]
  },

  olvido_postmortem: {
    // Pricing (from centralized pricing.js)
    price: PRICING.OLVIDO_POSTMORTEM.price,
    priceLabel: PRICING.OLVIDO_POSTMORTEM.priceLabel,

    // URL limits
    urls: PRICING.OLVIDO_POSTMORTEM.urlLimit,
    urlsLabel: 'Hasta 5 URLs',

    // Case limits - 1 case per user
    casesPerUser: 1,
    casesPerUserLabel: '1 caso único',

    // Case management capabilities
    canCreateCases: true,
    canManageCases: true,

    // Support
    supportResponseTime: '12h',
    supportType: 'Email prioritario',

    // Features available (includes all from olvido)
    features: [
      'url_management',
      'case_creation',
      'document_generation',
      'timeline_tracking',
      'email_support',
      'deceased_info',
      'priority_support'
    ],

    // Features labels for UI
    featuresLabels: [
      'Gestión de hasta 5 URLs',
      'Creación de 1 caso post-mortem',
      'Información de persona fallecida',
      'Generación de documentos especializados',
      'Seguimiento prioritario',
      'Soporte prioritario por email (12h)',
      'Gestión de legado digital'
    ],

    // Restrictions
    restrictions: [
      'Solo 1 caso por usuario',
      'Máximo 5 URLs',
      'Sin asesoría legal especializada'
    ]
  },

  testamento: {
    // Pricing (from centralized pricing.js)
    price: PRICING.TESTAMENTO.price,
    priceLabel: PRICING.TESTAMENTO.priceLabel,

    // URL limits - Testamento doesn't use URLs
    urls: PRICING.TESTAMENTO.urlLimit,
    urlsLabel: 'Sin gestión de URLs',

    // Case limits - 1 case per user
    casesPerUser: 1,
    casesPerUserLabel: '1 testamento único',

    // Case management capabilities
    canCreateCases: true,
    canManageCases: true,

    // Support
    supportResponseTime: '6h',
    supportType: 'Email premium con asesoría',

    // Features available (different from olvido services)
    features: [
      'case_creation',
      'document_generation',
      'timeline_tracking',
      'digital_instructions',
      'executor_designation',
      'legal_advice'
    ],

    // Features labels for UI
    featuresLabels: [
      'Creación de 1 testamento digital',
      'Instrucciones digitales detalladas',
      'Designación de albacea digital',
      'Generación de documentos legales',
      'Asesoría legal incluida',
      'Soporte premium (6h)',
      'Gestión integral de legado'
    ],

    // Restrictions
    restrictions: [
      'Solo 1 testamento por usuario',
      'No incluye gestión de URLs',
      'Enfocado exclusivamente en testamento digital'
    ]
  }
}

// Feature access checker
export const hasFeatureAccess = (userTier, feature) => {
  if (!userTier || !TIER_LIMITS[userTier]) return false
  return TIER_LIMITS[userTier].features.includes(feature)
}

// URL limit checker (replaces getPlatformLimit)
export const getUrlLimit = (tier) => {
  if (!tier || !TIER_LIMITS[tier]) return 0
  return TIER_LIMITS[tier].urls
}

// Check if user can create cases (considering 1 case per user limit)
export const canCreateCase = (tier, hasExistingCase = false) => {
  if (!tier || !TIER_LIMITS[tier]) return false

  // Check if tier allows case creation
  if (!TIER_LIMITS[tier].canCreateCases) return false

  // All tiers now have 1 case per user limit
  return !hasExistingCase
}

// Get remaining URLs
export const getRemainingUrls = (tier, usedUrls) => {
  const limit = getUrlLimit(tier)
  if (limit === 0) return 0 // No URL management (testamento)
  return Math.max(0, limit - usedUrls)
}

// Check if user can create cases based on tier (with existing case check)
export const canUserCreateCases = (tier, hasExistingCase = false) => {
  if (!tier || !TIER_LIMITS[tier]) return false
  if (!TIER_LIMITS[tier].canCreateCases) return false

  // 1 case per user restriction
  return !hasExistingCase
}

// Check if user can manage cases based on tier
export const canUserManageCases = (tier) => {
  if (!tier || !TIER_LIMITS[tier]) return false
  return TIER_LIMITS[tier].canManageCases || false
}

// Get case limit for tier
export const getCaseLimit = (tier) => {
  if (!tier || !TIER_LIMITS[tier]) return 0
  return TIER_LIMITS[tier].casesPerUser
}

// Quick actions by tier
export const TIER_QUICK_ACTIONS = {
  olvido: [
    { id: 'new_case', label: 'Crear caso', icon: 'DocumentPlusIcon', href: '/cases/new', requiresLimit: true },
    { id: 'view_case', label: 'Mi caso', icon: 'FolderIcon', href: '/cases' },
    { id: 'manage_urls', label: 'Gestionar URLs', icon: 'LinkIcon', href: '/cases/urls' },
    { id: 'documents', label: 'Documentos', icon: 'DocumentTextIcon', href: '/documents' },
    { id: 'timeline', label: 'Timeline', icon: 'ClockIcon', href: '/timeline' }
  ],
  olvido_postmortem: [
    { id: 'new_case', label: 'Crear caso post-mortem', icon: 'DocumentPlusIcon', href: '/cases/new', requiresLimit: true },
    { id: 'view_case', label: 'Mi caso', icon: 'FolderIcon', href: '/cases' },
    { id: 'deceased_info', label: 'Información del fallecido', icon: 'HeartIcon', href: '/cases/deceased' },
    { id: 'manage_urls', label: 'Gestionar URLs', icon: 'LinkIcon', href: '/cases/urls' },
    { id: 'documents', label: 'Documentos', icon: 'DocumentTextIcon', href: '/documents' },
    { id: 'timeline', label: 'Timeline', icon: 'ClockIcon', href: '/timeline' }
  ],
  testamento: [
    { id: 'new_testament', label: 'Crear testamento', icon: 'DocumentPlusIcon', href: '/cases/new', requiresLimit: true },
    { id: 'view_testament', label: 'Mi testamento', icon: 'FolderIcon', href: '/cases' },
    { id: 'digital_instructions', label: 'Instrucciones digitales', icon: 'ClipboardDocumentListIcon', href: '/cases/instructions' },
    { id: 'executor', label: 'Designar albacea', icon: 'UserIcon', href: '/cases/executor' },
    { id: 'documents', label: 'Mis documentos', icon: 'DocumentTextIcon', href: '/documents' },
    { id: 'timeline', label: 'Timeline', icon: 'ClockIcon', href: '/timeline' }
  ]
}

export default {
  TIER_NAMES,
  TIER_DISPLAY,
  TIER_LIMITS,
  TIER_QUICK_ACTIONS,
  hasFeatureAccess,
  getUrlLimit,
  canCreateCase,
  canUserCreateCases,
  canUserManageCases,
  getRemainingUrls,
  getCaseLimit
}